/**
 * Scorecard question catalog — wording aligned to Power Apps POC Scorecard.
 * Weights are for client-side total only until backend owns scoring.
 * Never render weight values in the requestor UI.
 */

import { TEMPLATE_FILES, templateHref } from '../templateFile';

export type YesNo = boolean;

export type HelpLink = { label: string; href: string };

export type ScorecardQuestion = {
  code: string;
  section: string;
  /** Display number as in Power Apps (1–18) */
  number: number;
  text: string;
  /** Optional line under the question (e.g. “Please check…”) */
  helpNote?: string;
  /** Internal only — do not show in UI */
  weight: number;
  kind: 'yesno' | 'payer';
  helpLinks?: HelpLink[];
};

export const SCORECARD_QUESTIONS: ScorecardQuestion[] = [
  {
    code: 'SC_Q1',
    section: 'Desirability',
    number: 1,
    text: 'Is a customer need or business challenge addressed?',
    weight: 5,
    kind: 'yesno',
  },
  {
    code: 'SC_Q2',
    section: 'Desirability',
    number: 2,
    text: 'Have you checked if there is already an existing solution available* at ISS?',
    helpNote: 'Please check for existing solutions:',
    weight: 4,
    kind: 'yesno',
    helpLinks: [
      {
        label: 'HOPEX',
        href: 'https://ems-issworld.hopexcloud.com/index.htm',
      },
      {
        label: 'Global Products and Software Stocktake',
        href: templateHref(TEMPLATE_FILES.stocktake),
      },
    ],
  },
  {
    code: 'SC_Q3',
    section: 'Corporate identity',
    number: 3,
    text: 'Is it in line with our ISS strategic priorities and aligned with ISS identity?',
    weight: 4,
    kind: 'yesno',
    helpLinks: [
      {
        label: 'Link to OneISS Strategy',
        href: 'https://myiss.issworld.com/content/page/689ef1d865c8a31ce15553bc',
      },
    ],
  },
  {
    code: 'SC_Q4',
    section: 'Corporate identity',
    number: 4,
    text: "Is it aligned with our company's innovation guidance?",
    weight: 3,
    kind: 'yesno',
    helpLinks: [{ label: 'Link to ISS Radar/Framework', href: '#' }],
  },
  {
    code: 'SC_Q5',
    section: 'Adaptability',
    number: 5,
    text: 'Is the PoC well positioned to succeed against competitors and new emerging players, or is it following any market trends?',
    weight: 2,
    kind: 'yesno',
  },
  {
    code: 'SC_Q6',
    section: 'Adaptability',
    number: 6,
    text: 'Do you already have a preferred supplier in mind to run the Proof of concept?',
    weight: 1,
    kind: 'yesno',
  },
  {
    code: 'SC_Q7',
    section: 'Adaptability',
    number: 7,
    text: 'Does the solution the supplier provides, fulfill the use case you want to solve?',
    weight: 3,
    kind: 'yesno',
  },
  {
    code: 'SC_Q8',
    section: 'Adaptability',
    number: 8,
    text: 'Is the solution the supplier provides scalable?',
    weight: 2,
    kind: 'yesno',
  },
  {
    code: 'SC_Q9',
    section: 'Costs',
    number: 9,
    text: 'Do we need to pay for the PoC?',
    weight: 0,
    kind: 'yesno',
  },
  {
    code: 'SC_Q10',
    section: 'Costs',
    number: 10,
    text: 'Have you received a discounted PoC offer?',
    weight: 3,
    kind: 'yesno',
  },
  {
    code: 'SC_Q11',
    section: 'Costs',
    number: 11,
    text: 'Has it already been clarified that the ISS entity or our customer is willing to pay for the PoC?',
    weight: 3,
    kind: 'yesno',
  },
  {
    code: 'SC_Q12',
    section: 'Costs',
    number: 12,
    text: 'ISS entity or Customer',
    weight: 0,
    kind: 'payer',
  },
  {
    code: 'SC_Q13',
    section: 'Feasibility',
    number: 13,
    text: 'Do we have the right internal capabilities allocated (resources, technology, partner) for delivering the value proposition?',
    weight: 3,
    kind: 'yesno',
  },
  {
    code: 'SC_Q14',
    section: 'Feasibility',
    number: 14,
    text: 'Has a timeframe for the PoC scope been defined and matches its complexity & scope?',
    weight: 4,
    kind: 'yesno',
    
  },
  {
    code: 'SC_Q15',
    section: 'Expected return',
    number: 15,
    text: 'Are there financial or other value profit gained from the PoC?',
    weight: 4,
    kind: 'yesno',
  },
  {
    code: 'SC_O1',
    section: 'PoC ownership',
    number: 16,
    text: 'Do you have a PoC owner defined? Someone who acts as a project manager, drives and facilitates the PoC. Someone who aligns with the respective stakeholders, including business line representatives, on target, scope and budget?',
    weight: 4,
    kind: 'yesno',
  },
  {
    code: 'SC_O2',
    section: 'PoC ownership',
    number: 17,
    text: 'Are you aligned with the business & IT stakeholders? This is critical to ensure business alignment and operational fit. Have you reached out to your local service line owners?',
    weight: 3,
    kind: 'yesno',
  },
  {
    code: 'SC_O3',
    section: 'PoC ownership',
    number: 18,
    text: 'Do you have a local Single Point of Contact (SPOC) identified and allocated? They will help set up, implement and evaluate the PoC, as well as liaise with relevant local stakeholders.',
    weight: 2,
    kind: 'yesno',
  },
];

export type ScorecardAnswers = Record<string, YesNo | undefined>;
export type PayerType = 'ISS_ENTITY' | 'CUSTOMER';

export function isQuestionVisible(
  code: string,
  answers: ScorecardAnswers
): boolean {
  const q6 = answers.SC_Q6;
  const q11 = answers.SC_Q11;

  if (code === 'SC_Q7' || code === 'SC_Q8'|| code === 'SC_Q9' || code === 'SC_Q10') {
    return q6 === true;
  }
  if (code === 'SC_Q12') {
    return q11 === true;
  }
  return true;
}

export function getVisibleQuestions(answers: ScorecardAnswers): ScorecardQuestion[] {
  return SCORECARD_QUESTIONS.filter((q) => isQuestionVisible(q.code, answers));
}

export type OutcomeBand = 'HIGHEST' | 'MEDIUM' | 'LOW';

export function calculateScorecard(
  answers: ScorecardAnswers,
  payerType: PayerType | null
): {
  totalScore: number;
  band: OutcomeBand;
  bandTitle: string;
  bandMessage: string;
} {
  let totalScore = 0;

  for (const q of SCORECARD_QUESTIONS) {
    if (!isQuestionVisible(q.code, answers)) continue;
    if (q.kind === 'payer') continue;
    if (answers[q.code] === true) {
      totalScore += q.weight;
    }
  }

  let band: OutcomeBand;
  let bandTitle: string;
  let bandMessage: string;

  // Confirmed advisory bands (max score 53). Does not block proceed/submit.
  if (totalScore >= 41) {
    band = 'HIGHEST';
    bandTitle = 'Great!';
    bandMessage =
      'Please go ahead and move to PoC preparation. Your idea is well-aligned with market needs and has strong potential for success.';
  } else if (totalScore >= 21) {
    band = 'MEDIUM';
    bandTitle = 'Promising!';
    bandMessage =
      'Consider refining your concept and addressing any potential weaknesses before moving forward. There is good potential, but some areas need improvement.';
  } else {
    band = 'LOW';
    bandTitle = 'Needs Improvement';
    bandMessage =
      'Your scorecard is pretty low. Based on that value we recommend checking the low-achieved items and coming back. Significant issues or risks have been identified. Reevaluate your concept and consider making substantial changes before proceeding.';
  }

  void payerType;
  return { totalScore, band, bandTitle, bandMessage };
}

export function isScorecardComplete(
  answers: ScorecardAnswers,
  payerType: PayerType | null
): boolean {
  const visible = getVisibleQuestions(answers);
  for (const q of visible) {
    if (q.kind === 'payer') {
      if (!payerType) return false;
    } else if (answers[q.code] === undefined) {
      return false;
    }
  }
  return true;
}
